import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Loader2 } from "lucide-react";
import api from "../api";
import { Button, Input, Card, Badge } from "../components/ui";
import { useSuccessFailure } from "../context/SuccessFailureContext";

const COLUMNS = [
  { key: "todo", label: "To do", tone: "gray" },
  { key: "in_progress", label: "In progress", tone: "yellow" },
  { key: "done", label: "Done", tone: "green" },
];
const NEXT = { todo: "in_progress", in_progress: "done", done: "todo" };

const RoomPage = () => {
  const { roomId } = useParams();
  const { showFailureModal } = useSuccessFailure();
  const [room, setRoom] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/communities/rooms/${roomId}/`);
      setRoom(res.data.room);
      setTasks(res.data.tasks || []);
    } catch (e) {
      showFailureModal("Error", "Could not load this room.");
    } finally {
      setLoading(false);
    }
  }, [roomId, showFailureModal]);

  useEffect(() => { load(); }, [load]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try { await api.post(`/communities/rooms/${roomId}/tasks/`, { title }); setTitle(""); load(); }
    catch (err) { showFailureModal("Failed", "Could not add task."); }
  };

  const advance = async (task) => {
    try {
      await api.patch(`/communities/tasks/${task.id}/`, { status: NEXT[task.status] });
      load();
    } catch (err) { showFailureModal("Failed", "Could not update task."); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  if (!room) return null;

  const m = room.metrics || {};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{room.name}</h1>
        {room.purpose && <p className="text-gray-500 dark:text-gray-400">{room.purpose}</p>}

        {/* Metrics */}
        <div className="flex gap-4 my-5 text-sm">
          <span className="text-gray-500 dark:text-gray-400">{m.tasks_total ?? 0} tasks</span>
          <span className="text-green-600 dark:text-green-400">{m.done ?? 0} done</span>
          <span className="text-gray-700 dark:text-gray-300 font-semibold">{m.completion ?? 0}% complete</span>
        </div>

        {/* Add task */}
        <form onSubmit={addTask} className="flex gap-2 mb-6 max-w-xl">
          <Input containerClassName="flex-1" placeholder="Add a task…" value={title}
            onChange={(e) => setTitle(e.target.value)} />
          <Button type="submit" leftIcon={<Plus size={16} />}>Add</Button>
        </form>

        {/* Board */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {COLUMNS.map((col) => (
            <div key={col.key}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-gray-900 dark:text-white">{col.label}</h2>
                <Badge tone={col.tone}>{tasks.filter((t) => t.status === col.key).length}</Badge>
              </div>
              <div className="space-y-3">
                {tasks.filter((t) => t.status === col.key).map((t) => (
                  <Card key={t.id} className="p-4">
                    <p className="font-medium text-gray-900 dark:text-white">{t.title}</p>
                    {t.assignee_name && <p className="text-xs text-gray-400 mt-1">{t.assignee_name}</p>}
                    {t.due_date && <p className="text-xs text-gray-400">Due {t.due_date}</p>}
                    <button onClick={() => advance(t)}
                      className="mt-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                      {t.status === "done" ? "Reopen" : `Move to ${COLUMNS.find((c) => c.key === NEXT[t.status]).label}`}
                    </button>
                  </Card>
                ))}
                {tasks.filter((t) => t.status === col.key).length === 0 &&
                  <p className="text-sm text-gray-400">Nothing here.</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoomPage;
